<template>
    <div>
        <div v-if="!fetchingPoolUsers" class="d-flex flex-wrap align-center">
            <ResourcePoolInviteDialog v-if="isResourcePoolManager" @invited="fetchPoolUsers">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primary" class="mr-2" v-bind="attrs" v-on="on">+ manager</v-btn>
                </template>
            </ResourcePoolInviteDialog>
            <v-spacer />
            <v-text-field
                class="mx-2"
                placeholder="Search for a manager..."
                autofocus
                prepend-inner-icon="mdi-magnify"
                v-model="search"
                solo
                flat
                hide-details
                dense
                clearable>
                <template v-slot:append>
                    <PoolTypeFilterMenu :available-types="[]" :roles.sync="selectedRoles" :available-roles="availableRoles" />
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" @click="fetchPoolUsers" text icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>refresh</span>
            </v-tooltip>
        </div>

        <div v-if="!fetchingPoolUsers">
            <v-data-table
                :headers="userHeaders"
                :items="filteredPoolUsers"
                :search="search"
                item-key="email"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.user`]="{ item }">
                    <div class="font-weight-medium py-2">{{ item.full_name || item.name || '-' }}</div>
                </template>
                <template v-slot:[`item.email`]="{ item }">
                    <div class="py-2">{{ item.email || '-' }}</div>
                </template>
                <template v-slot:[`item.role`]="{ item }">
                    <div class="d-flex align-center py-2">
                        <v-chip outlined label small>
                            <v-icon left small>{{ resourcePoolRoleMetadata(item.roleKey).icon }}</v-icon>
                            <span>{{ item.role }}</span>
                        </v-chip>
                    </div>
                </template>
                <template v-slot:no-results>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-magnify-close</v-icon>
                        <span>No managers found for the current search.</span>
                    </div>
                </template>
                <template v-slot:no-data>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-account-off-outline</v-icon>
                        <span class="font-weight-medium secondary--text mb-1">No managers found</span>
                        <span class="text-center">This resource pool has no managers yet.</span>
                    </div>
                </template>
            </v-data-table>
        </div>
        <div v-else>
            <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import ResourcePoolInviteDialog from '@/modules/user/components/ResourcePoolInviteDialog.vue'
import PoolTypeFilterMenu from '@/modules/user/components/PoolTypeFilterMenu.vue'
import { resourcePoolRoleMetadata, resourcePoolRole, resourcePoolRoleLabel } from '@/modules/user/components/resourcePoolMeta'

export default {
    name: 'PoolUsers',
    components: { ResourcePoolInviteDialog, PoolTypeFilterMenu },
    data() {
        return {
            search: '',
            fetchingPoolUsers: false,
            poolUsers: [],
            selectedRoles: ['rootManager', 'manager'],
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1]
        }
    },
    computed: {
        ...mapGetters('userStore', ['isResourcePoolManager']),
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        activeResourcePoolId() {
            const routePoolId = Number(this.$route.params.rpid)
            if (Number.isFinite(routePoolId) && routePoolId > 0) {
                return routePoolId
            }

            const selectedPoolId = Number(this.selectedResourcePool)
            if (Number.isFinite(selectedPoolId) && selectedPoolId > 0) {
                return selectedPoolId
            }

            return Number(this.availableResourcePools[0]?.rpid || 0)
        },
        usersWithRoles() {
            return this.poolUsers.map(user => ({
                ...user,
                user: user.full_name || user.name || '-',
                roleKey: resourcePoolRole(user, 'manager'),
                role: resourcePoolRoleLabel(user, 'manager')
            }))
        },
        availableRoles() {
            return Array.from(new Set(this.usersWithRoles.map(user => user.roleKey)))
        },
        filteredPoolUsers() {
            return this.usersWithRoles.filter(user => this.selectedRoles.includes(user.roleKey))
        },
        userHeaders() {
            return [
                { text: 'Manager', value: 'user', sortable: false },
                { text: 'Email', value: 'email', sortable: false },
                { text: 'Role', value: 'role', sortable: false }
            ]
        }
    },
    methods: {
        resourcePoolRoleMetadata,
        async fetchPoolUsers() {
            if (!this.activeResourcePoolId) {
                this.poolUsers = []
                return
            }

            this.fetchingPoolUsers = true
            try {
                const { data } = await this.$axios.get(`/resource/${this.activeResourcePoolId}/contacts`)
                this.poolUsers = Array.isArray(data) ? data : []
            } catch (error) {
                this.poolUsers = []
            } finally {
                this.fetchingPoolUsers = false
            }
        }
    },
    watch: {
        activeResourcePoolId: {
            handler() {
                this.fetchPoolUsers()
            },
            immediate: true
        }
    }
}
</script>
